import { api } from '../../shared/api/client'
import { createIdempotencyKey } from '../layout-generator/api'

export interface MoldNeed {
  article: string
  kitId?: string
  revision?: number
  status: 'ready' | 'active' | 'missing' | 'printed'
  reason?: string
  artifactHash?: string
  activeOrderId?: string
}
export interface MoldPreview { items: MoldNeed[]; canCreate: boolean }
export type MoldItemStatus = 'queued' | 'in_progress' | 'ready' | 'defective' | 'cancelled'
export interface MoldOrderItem {
  id: string
  article: string
  status: MoldItemStatus
  version: number
  quantity: number
  reason?: string
}
export interface MoldOrder {
  id: string
  ownerId: number
  assignedUserId: number
  assignedUserName?: string
  version: number
  status: string
  createdAt: string
  itemCount?: number
  readyCount?: number
}
export interface MoldOrderDetail {
  order: MoldOrder
  items: MoldOrderItem[]
  audit: Array<{ id?: string; event?: string; actorId?: number; details?: Record<string, unknown>; status?: string; action?: string; reason?: string; createdAt?: string; actorName?: string }>
}
const BASE = '/gingerbread/mold-production'
const unwrap = (response: any) => response.data?.data ?? response.data

export const moldAPI = {
  key: () => createIdempotencyKey('mold-order'),
  async assignees(): Promise<{ users: Array<{ id: number; name: string }> }> {
    return unwrap(await api.get(`${BASE}/assignees`))
  },
  async preview(articles: string[], signal?: AbortSignal): Promise<MoldPreview> {
    return unwrap(await api.post(`${BASE}/preview`, { articles }, { signal }))
  },
  async readiness(articles: string[], signal?: AbortSignal): Promise<MoldPreview> {
    return unwrap(await api.post(`${BASE}/readiness`, { articles }, { signal }))
  },
  async create(input: { articles: string[]; assignedUserId: number; idempotencyKey: string; quantity?: number; reason?: string; reprint?: boolean }): Promise<MoldOrderDetail> {
    return unwrap(await api.post(`${BASE}/orders`, input, { headers: { 'Idempotency-Key': input.idempotencyKey } }))
  },
  async confirmLegacy(input: { articles: string[]; assignedUserId: number; idempotencyKey: string; quantity?: number; reason?: string }): Promise<MoldOrderDetail> {
    return unwrap(await api.post(`${BASE}/confirm-existing`, input, { headers: { 'Idempotency-Key': input.idempotencyKey } }))
  },
  async list(): Promise<{ orders: MoldOrder[] }> {
    return unwrap(await api.get(`${BASE}/orders`))
  },
  async detail(id: string): Promise<MoldOrderDetail> {
    return unwrap(await api.get(`${BASE}/orders/${encodeURIComponent(id)}`))
  },
  async transition(orderId: string, item: MoldOrderItem, status: MoldItemStatus, reason?: string): Promise<MoldOrderDetail> {
    return unwrap(await api.patch(`${BASE}/orders/${encodeURIComponent(orderId)}/items/${encodeURIComponent(item.id)}`, {
      expectedVersion: item.version, status, reason,
    }))
  },
  async assign(orderId: string, assignedUserId: number, expectedVersion: number, reason: string): Promise<MoldOrderDetail> {
    return unwrap(await api.patch(`${BASE}/orders/${encodeURIComponent(orderId)}/assignee`, { assignedUserId, expectedVersion, reason }))
  },
  async download(id: string) {
    return api.get(`${BASE}/orders/${encodeURIComponent(id)}/files`, { responseType: 'blob' })
  },
}

export const MOLD_NEED_LABEL: Record<MoldNeed['status'], string> = {
  printed: 'Форма готова', active: 'Заказана / печатается', ready: 'Нужно заказать', missing: 'Нет актуального STL',
}
export const MOLD_ITEM_LABEL: Record<MoldItemStatus, string> = {
  queued: 'В очереди', in_progress: 'В работе', ready: 'Готово', defective: 'Брак', cancelled: 'Отменено',
}
export const MOLD_ORDER_LABEL: Record<string, string> = {
  queued: 'В очереди', in_progress: 'В работе', partially_ready: 'Частично готова',
  partial: 'Частично готова', completed: 'Завершена', ready: 'Готова', cancelled: 'Отменена', defective: 'Брак',
}
